import { ReactNode, useEffect, useState, forwardRef } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import PortalSidebar from "@/components/layout/PortalSidebar";
import StaticToolBackground from "@/components/layout/StaticToolBackground";
import OperatorHeader from "@/components/operator/OperatorHeader";
import type { SidebarNavItem } from "@/components/layout/PortalSidebar";
import {
  LayoutDashboard,
  Building2,
  FileText,
  AlertTriangle,
  CreditCard,
  Tag,
  Target,
  Settings,
  ScrollText,
  UserPlus,
  Inbox,
  BarChart3,
  Megaphone,
  Calculator,
  Star,
  Globe,
} from "lucide-react";

function useNavItems(): SidebarNavItem[] {
  const { t } = useTranslation();
  return [
    { path: "/operator", label: t("op.navDashboard"), icon: LayoutDashboard },
    { path: "/operator/registrations", label: t("op.navRegistrations"), icon: UserPlus },
    { path: "/operator/requests", label: t("op.navRequests"), icon: Inbox },
    { path: "/operator/providers", label: t("op.navProviders"), icon: Building2 },
    { path: "/operator/opportunities", label: t("op.navOpportunities"), icon: FileText },
    { path: "/operator/claims", label: t("op.navClaims"), icon: AlertTriangle },
    { path: "/operator/billing", label: t("op.navBilling"), icon: CreditCard },
    { path: "/operator/costs", label: t("op.navCosts"), icon: BarChart3 },
    { path: "/operator/pricing", label: t("op.navPricing"), icon: Tag },
    { path: "/operator/matching", label: t("op.navMatching"), icon: Target },
    { path: "/operator/settings", label: t("op.navSettings"), icon: Settings },
    
    { path: "/operator/sales", label: t("op.navSales"), icon: Megaphone },
    { path: "/operator/roi", label: t("op.navROI"), icon: Calculator },
    { path: "/operator/feedback", label: t("op.navFeedback"), icon: Star },
    { path: "/operator/seo", label: "SEO Engine", icon: Globe },
  ];
}

interface OperatorLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

const OperatorLayout = forwardRef<HTMLDivElement, OperatorLayoutProps>(function OperatorLayout({ children, title, subtitle }, ref) {
  const { user, isLoading } = useAuth();
  const navigate = useLocaleNavigate();
  const { t } = useTranslation();
  const navItems = useNavItems();
  const [isOperator, setIsOperator] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
      return;
    }
    if (user) {
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .then(({ data }) => {
          const roles = (data || []).map((r: any) => r.role);
          const hasAccess = roles.includes("admin") || roles.includes("operator") || roles.includes("operator_readonly");
          setIsOperator(hasAccess);
          if (!hasAccess) navigate("/dashboard");
        });
    }
  }, [user, isLoading, navigate]);

  if (isLoading || isOperator === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("op.loadingPortal")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <StaticToolBackground />
      <PortalSidebar
        portalLabel={t("op.portalLabel")}
        roleLabel={t("op.roleLabel")}
        navItems={navItems}
        homePath="/operator"
        profilePath="/operator/profile"
        portalSwitchPath="/dashboard"
        portalSwitchLabel="Provider Portal"
        cta={{
          label: t("op.ctaNewProvider"),
          description: t("op.ctaNewProviderDesc"),
          icon: UserPlus,
          onClick: () => navigate("/operator/providers/new"),
        }}
      />

      {/* Main content offset by sidebar */}
      <main className="md:ml-[272px] transition-all duration-300 flex flex-col min-h-screen">
        <OperatorHeader />
        <div className="flex-1 p-6 md:p-8">
          <div className="mb-8">
            <h1 className="text-headline">{title}</h1>
            {subtitle && <p className="text-caption mt-1">{subtitle}</p>}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
});

export default OperatorLayout;
